import React from 'react';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import PublicRusheeForm from './pages/PublicRusheeForm';
import RusheeSubmissions from './components/Events/RusheeSubmissions';
import BrotherSubmissions from './components/Events/BrotherSubmissions';
import RusheeOnboardingForm from './components/Rushees/RusheeOnboardingForm';
import ManageNotes from './components/Rushees/ManageNotes';
import Wrapped from './pages/Wrapped';
import AppShell from './components/layout/AppShell';
import DashboardView from './components/layout/DashboardView';
import EventList from './components/Events/EventList';
import CreateEvent from './components/Events/CreateEvent';
import UpdateProfile from './components/Dashboards/UpdateProfile';
import ImportBrothers from './components/Dashboards/ImportBrothers';
import DisplayBrotherhood from './components/Dashboards/DisplayBrotherhood';
import DisplayRushees from './components/Rushees/DisplayRushees';
import NotesLog from './components/Rushees/NotesLog';
import DelibsMode from './components/Rushees/DelibsMode';
import { getBrotherData } from './utils/auth';
import { defaultPathForRole, ROLES } from './config/navigation';
import { GlobalStyle, theme } from './styles/theme';

const PrivateRoute = ({ children }) => {
  const brother = getBrotherData();
  if (!brother) {
    return <Navigate to="/login" />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const brother = getBrotherData();
  if (!brother) {
    return <Navigate to="/login" />;
  }
  if (brother.position !== 'President' && brother.position !== 'Rush Chair') {
    return <Navigate to={defaultPathForRole(brother.position)} replace />;
  }
  return children;
};

const RoleRoute = ({ roles, children }) => {
  const brother = getBrotherData();
  return roles.includes(brother?.position)
    ? children
    : <Navigate to={defaultPathForRole(brother?.position)} replace />;
};

const AppIndex = () => {
  const brother = getBrotherData();
  return <Navigate to={defaultPathForRole(brother?.position)} replace />;
};

const View = ({ title, children }) => <DashboardView title={title}>{children}</DashboardView>;
const ALL = [ROLES.PRESIDENT, ROLES.RUSH_CHAIR, ROLES.BROTHER];
const ADMINS = [ROLES.PRESIDENT, ROLES.RUSH_CHAIR];

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
        {/* Public routes */}
        <Route path="/:fratId/rush/:eventId" element={<PublicRusheeForm />} />
        <Route path="/:fratId/rush/onboarding" element={<RusheeOnboardingForm />} />
        
        {/* Private routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/app" element={<PrivateRoute><AppShell /></PrivateRoute>}>
          <Route index element={<AppIndex />} />
          <Route path="events" element={<RoleRoute roles={ALL}><View title="Events"><EventList /></View></RoleRoute>} />
          <Route path="create-event" element={<RoleRoute roles={ADMINS}><View title="Create event"><CreateEvent /></View></RoleRoute>} />
          <Route path="brotherhood" element={<RoleRoute roles={[ROLES.PRESIDENT]}><View title="Brotherhood"><DisplayBrotherhood /></View></RoleRoute>} />
          <Route path="import-brothers" element={<RoleRoute roles={[ROLES.PRESIDENT]}><View title="Import brothers"><ImportBrothers /></View></RoleRoute>} />
          <Route path="rushees" element={<RoleRoute roles={ALL}><View title="Rushees"><DisplayRushees /></View></RoleRoute>} />
          <Route path="rushees/:rusheeId" element={<RoleRoute roles={ALL}><View title="Rushees"><DisplayRushees /></View></RoleRoute>} />
          <Route path="notes" element={<RoleRoute roles={ALL}><View title="Notes log"><NotesLog /></View></RoleRoute>} />
          <Route path="slides" element={<RoleRoute roles={ALL}><View title="Slides view"><DelibsMode /></View></RoleRoute>} />
          <Route path="manage-notes" element={<RoleRoute roles={ADMINS}><View title="Manage notes"><ManageNotes /></View></RoleRoute>} />
          <Route path="profile" element={<RoleRoute roles={ALL}><View title="Your profile"><UpdateProfile /></View></RoleRoute>} />
          <Route path="*" element={<AppIndex />} />
        </Route>
        <Route path="/dashboard" element={<PrivateRoute><AppIndex /></PrivateRoute>} />
        <Route
          path="/:fratId/rush/:eventId/rushee-submissions"
          element={
            <PrivateRoute>
              <RusheeSubmissions />
            </PrivateRoute>
          }
        />
        <Route
          path="/:fratId/rush/:eventId/brother-submissions"
          element={
            <PrivateRoute>
              <BrotherSubmissions />
            </PrivateRoute>
          }
        />
        <Route path="/manage-notes" element={<AdminRoute><Navigate to="/app/manage-notes" replace /></AdminRoute>} />
        <Route
          path="/wrapped"
          element={
            <PrivateRoute>
              <Wrapped />
            </PrivateRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
